import type { FlowEdge, FlowNode } from '../types/flow-types';

export interface FlowGraphAnalysis {
	rootNodeIds: string[];
	joinNodeIds: string[];
	cycles: string[][];
}

export interface SwitchSelection {
	edges: FlowEdge[];
	matchedCaseId?: string;
	usedDefault: boolean;
	assumed: boolean;
}

/** Analyze executable topology. Notes and dangling edges do not affect runtime semantics. */
export function analyzeFlowGraph(nodes: FlowNode[], edges: FlowEdge[]): FlowGraphAnalysis {
	const semanticNodes = nodes.filter((node) => node.type !== 'note');
	const ids = new Set(semanticNodes.map((node) => node.id));
	const semanticEdges = edges.filter((edge) => ids.has(edge.source) && ids.has(edge.target));
	const incoming = new Map(semanticNodes.map((node) => [node.id, 0]));
	const outgoing = new Map(semanticNodes.map((node) => [node.id, [] as string[]]));
	for (const edge of semanticEdges) {
		incoming.set(edge.target, (incoming.get(edge.target) ?? 0) + 1);
		outgoing.get(edge.source)?.push(edge.target);
	}

	// Tarjan SCC: every multi-node SCC (or self-edge) is a runtime cycle.
	let index = 0;
	const indexes = new Map<string, number>();
	const lowLinks = new Map<string, number>();
	const stack: string[] = [];
	const onStack = new Set<string>();
	const cycles: string[][] = [];

	function visit(nodeId: string) {
		indexes.set(nodeId, index);
		lowLinks.set(nodeId, index);
		index += 1;
		stack.push(nodeId);
		onStack.add(nodeId);

		for (const target of outgoing.get(nodeId) ?? []) {
			if (!indexes.has(target)) {
				visit(target);
				lowLinks.set(nodeId, Math.min(lowLinks.get(nodeId)!, lowLinks.get(target)!));
			} else if (onStack.has(target)) {
				lowLinks.set(nodeId, Math.min(lowLinks.get(nodeId)!, indexes.get(target)!));
			}
		}

		if (lowLinks.get(nodeId) !== indexes.get(nodeId)) return;
		const component: string[] = [];
		let popped = '';
		do {
			popped = stack.pop()!;
			onStack.delete(popped);
			component.push(popped);
		} while (popped !== nodeId);
		const selfCycle = component.length === 1 && (outgoing.get(nodeId) ?? []).includes(nodeId);
		if (component.length > 1 || selfCycle) cycles.push(component.sort());
	}

	for (const node of semanticNodes) if (!indexes.has(node.id)) visit(node.id);
	return {
		rootNodeIds: semanticNodes
			.filter((node) => (incoming.get(node.id) ?? 0) === 0)
			.map((node) => node.id),
		joinNodeIds: semanticNodes
			.filter((node) => (incoming.get(node.id) ?? 0) > 1)
			.map((node) => node.id),
		cycles,
	};
}

function compareSwitchValue(actual: unknown, expected: unknown, operator: string): boolean {
	const numericActual = Number(actual);
	const numericExpected = Number(expected);
	const bothNumeric = Number.isFinite(numericActual) && Number.isFinite(numericExpected);
	const left = bothNumeric ? numericActual : String(actual ?? '');
	const right = bothNumeric ? numericExpected : String(expected ?? '');
	switch (operator) {
		case 'ne':
		case 'neq':
			return left !== right;
		case 'lt':
			return left < right;
		case 'le':
			return left <= right;
		case 'gt':
			return left > right;
		case 'ge':
			return left >= right;
		case 'contains':
			return String(left).includes(String(right));
		case 'eq':
		case 'equal':
		default:
			return left === right;
	}
}

/** Select exactly one switch path. Undefined test values deliberately use default/first. */
export function selectSwitchEdges(
	node: FlowNode,
	outgoing: FlowEdge[],
	value: unknown,
	hasValue: boolean,
): SwitchSelection {
	const cases = Array.isArray(node.data.cases)
		? (node.data.cases as Array<{
				id?: string;
				operator?: string;
				value?: unknown;
				value2?: unknown;
			}>)
		: [];
	if (hasValue) {
		const matchedIndex = cases.findIndex((item) =>
			item.operator === 'between_inc' || item.operator === 'between_exc'
				? item.operator === 'between_inc'
					? Number(value) >= Number(item.value) && Number(value) <= Number(item.value2)
					: Number(value) > Number(item.value) && Number(value) < Number(item.value2)
				: compareSwitchValue(value, item.value, item.operator ?? 'eq'),
		);
		if (matchedIndex >= 0) {
			const matched = cases[matchedIndex];
			return {
				edges: outgoing.filter((edge) => edge.sourceHandle === `case-${matchedIndex}`),
				matchedCaseId: matched.id ?? String(matchedIndex),
				usedDefault: false,
				assumed: false,
			};
		}
	}
	const defaults = outgoing.filter((edge) => edge.sourceHandle === 'default');
	return {
		edges: defaults.length ? defaults : outgoing.slice(0, 1),
		usedDefault: true,
		assumed: !hasValue,
	};
}
